import { CartProvider, useCart } from '@/context/CartContext';
import { InitialRenderTime } from '@/lib/initialRender';
import { CmsRouteBoundary, cmsPreviewLocation, getCmsSnapshot } from '@/cms/content';
import { CustomerProvider } from '@/context/CustomerContext';
import { FavoritesProvider } from '@/context/FavoritesContext';
import { ConsentProvider } from '@/context/ConsentContext';
import { GoogleAdsMeasurement } from '@/components/GoogleAdsMeasurement';
import { Route, Switch, Router as WouterRouter, useLocation, useParams } from 'wouter';
import { Suspense, useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import Home from '@/pages/home';
import { collectionUrl } from '@/lib/collectionUrl';
import { Header } from '@/components/sections/Header';
import { PageBreadcrumbOutlet, PageBreadcrumbProvider } from '@/components/sections/PageBreadcrumb';
import { MobileBottomNav } from '@/components/sections/MobileBottomNav';
import { CategoryDrawerLoader } from '@/components/sections/CategoryDrawerLoader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { switchToPanel } from '@/lib/mobilePanels';
import { CookieConsentManager } from '@/components/sections/CookieConsentManager';
import { articleUrl } from '@/lib/articleUrl';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { isUnavailableBouquetCreatorPath } from '@/lib/storeFeatures';

/* ── Lazy overlays — not needed for initial paint ── */
const Toaster       = lazyWithReload(() => import('@/components/ui/toaster').then(m => ({ default: m.Toaster })));
const AddToCartModal = lazyWithReload(() => import('@/components/ui/AddToCartModal').then(m => ({ default: m.AddToCartModal })));

/* ── Lazy page imports — each page gets its own chunk ── */

const CreatorComingSoon = lazyWithReload(() => import('@/pages/CreatorComingSoon'));
const CollectionPage   = lazyWithReload(() => import('@/pages/collection'));
const SlubPage         = lazyWithReload(() => import('@/pages/slub'));
const PogrzebPage             = lazyWithReload(() => import('@/pages/pogrzeb'));
const FuneralCollectionPage   = lazyWithReload(() => import('@/pages/FuneralCollectionPage'));
const AccountPage      = lazyWithReload(() => import('@/pages/account'));
const CheckoutPage     = lazyWithReload(() => import('@/pages/checkout'));
const DziekujemyPage   = lazyWithReload(() => import('@/pages/dziekujemy'));
const DostawaPage      = lazyWithReload(() => import('@/pages/dostawa'));
const KontaktPage      = lazyWithReload(() => import('@/pages/kontakt'));
const RegulaminPage    = lazyWithReload(() => import('@/pages/regulamin'));
const PrywatnoscPage   = lazyWithReload(() => import('@/pages/prywatnosc'));
const KreatorRoze        = lazyWithReload(() => import('@/pages/kreator-roze-shared'));
const KreatoryCatalog    = lazyWithReload(() => import('@/pages/kreatory'));
const KreatorTulipany    = lazyWithReload(() => import('@/pages/kreator-tulipany'));
const KreatorSloneczniki = lazyWithReload(() => import('@/pages/kreator-sloneczniki'));
const KreatorGerbery     = lazyWithReload(() => import('@/pages/kreator-gerbery'));
const KreatorLilie       = lazyWithReload(() => import('@/pages/kreator-lilie'));
const KreatorEustomy     = lazyWithReload(() => import('@/pages/kreator-eustomy'));
const AdvisorTagsAdmin = lazyWithReload(() => import('@/pages/advisor-tags'));
const DzielnicaPage    = lazyWithReload(() => import('@/pages/dzielnica'));
const DlaFirmPage          = lazyWithReload(() => import('@/pages/dla-firm'));
const PolitykZwrotowPage   = lazyWithReload(() => import('@/pages/polityka-zwrotow'));
const FaqPage              = lazyWithReload(() => import('@/pages/faq'));
const ONasPage             = lazyWithReload(() => import('@/pages/o-nas'));
const KwiatyPage           = lazyWithReload(() => import('@/pages/kwiaty'));
const OkazjePage           = lazyWithReload(() => import('@/pages/okazje'));
const BukietyPage            = lazyWithReload(() => import('@/pages/bukiety'));
const FlowerBoxyPage         = lazyWithReload(() => import('@/pages/flower-boxy'));
const CategoryCollectionPage = lazyWithReload(() => import('@/pages/CategoryCollectionPage'));
const DynamicCategoryPage    = lazyWithReload(() => import('@/pages/DynamicCategoryPage'));
const KoszeKwiatowePage      = lazyWithReload(() => import('@/pages/kosze-kwiatowe'));
const KwiatyWTorebcePage     = lazyWithReload(() => import('@/pages/kwiaty-w-torebce'));
const LasyWSzklePage         = lazyWithReload(() => import('@/pages/lasy-w-szkle'));
const BalonyzHelemPage       = lazyWithReload(() => import('@/pages/balony-z-helem'));
const KwiatyDoniczkowe       = lazyWithReload(() => import('@/pages/kwiaty-doniczkowe'));
const WieczneRozePage        = lazyWithReload(() => import('@/pages/wieczne-roze'));
const BestseleryPage       = lazyWithReload(() => import('@/pages/bestsellery'));
const BlogPage             = lazyWithReload(() => import('@/pages/blog'));
const RootContentPage      = lazyWithReload(() => import('@/pages/root-content'));
const PlatnosciPage        = lazyWithReload(() => import('@/pages/platnosci'));
const ZamowienieSpecjalnePage = lazyWithReload(() => import('@/pages/zamowienie-specjalne'));
const WarszytatyPage          = lazyWithReload(() => import('@/pages/warsztaty-florystyczne'));
const SklepPage               = lazyWithReload(() => import('@/pages/sklep'));
const DlaKogoHub              = lazyWithReload(() => import('@/pages/dla-kogo'));
const KwiaciarniaLublinHub    = lazyWithReload(() => import('@/pages/kwiaciarnia-lublin'));
const KwiaciarniaOkoliceHub   = lazyWithReload(() => import('@/pages/kwiaciarnia-okolice'));
const NotFound         = lazyWithReload(() => import('@/pages/not-found'));
const ResetHaslaPage   = lazyWithReload(() => import('@/pages/reset-hasla'));
const UlubionePage     = lazyWithReload(() => import('@/pages/ulubione'));
const KoszykRedirect   = lazyWithReload(() => import('@/pages/koszyk'));


/**
 * Client-side redirect: /collections/Ślub → /slub
 *
 * This is a SPA-level redirect (not a real HTTP 301) because the app is a
 * client-side React SPA served via a catch-all HTML fallback. The server
 * cannot issue a true 301 for this path. The redirect is instantaneous and
 * invisible to the user, and Google follows JavaScript redirects.
 * For a true HTTP 301, a server-side rewrite rule would be needed.
 */
function SlubRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate('/slub', { replace: true }); }, [navigate]);
  return null;
}

/** Przekierowuje stare /collections/:handle → publiczny URL kolekcji */
function ProduktyLegacyRedirect() {
  const params = useParams<{ handle: string }>();
  const [, navigate] = useLocation();
  useEffect(() => { navigate(`/${params.handle ?? ''}`, { replace: true }); }, []);
  return null;
}

function CollectionLegacyRedirect() {
  const [, navigate] = useLocation();
  const params = useParams<{ handle: string }>();
  useEffect(() => {
    navigate(collectionUrl(params.handle ?? ''), { replace: true });
  }, [navigate, params.handle]);
  return null;
}

/** Client fallback for local Vite navigation; production uses Worker HTTP 301. */
function BlogLegacyRedirect() {
  const [, navigate] = useLocation();
  const params = useParams<{ slug: string }>();
  useEffect(() => {
    navigate(articleUrl(params.slug ?? ''), { replace: true });
  }, [navigate, params.slug]);
  return null;
}

function ClientCanonicalRedirect({ to }: { to: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return null;
}

/**
 * Zamyka wszystkie panele mobilne (kategorie/koszyk/menu/ulubione/konto,
 * AddToCartModal) przy KAŻDEJ zmianie lokalizacji — w tym tej wywołanej przyciskiem/gestem
 * "wstecz" przeglądarki. System paneli (mobilePanels.ts) reaguje wyłącznie
 * na jawne zdarzenia z UI (tap w hamburger, tap w "Start" itd.), nie na
 * popstate — panel otwarty w momencie nawigacji wstecz zostawał więc
 * "zawieszony": blokada scrolla nigdy się nie zwalniała, co na
 * iOS Safari łamie position:fixed nagłówka i dolnej nawigacji do końca
 * sesji na tej stronie. To jeden globalny mechanizm — nie per-route hack,
 * działa identycznie niezależnie od tego, JAK doszło do zmiany lokalizacji.
 */
function ResetOverlaysOnNavigate() {
  const [path] = useLocation();
  const { lastAddedBatch, clearLastAdded } = useCart();
  useEffect(() => {
    switchToPanel(null);
    if (lastAddedBatch.length > 0) clearLastAdded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  return null;
}

function ScrollToTop() {
  const [path] = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [path]);
  return null;
}

/**
 * iOS Safari (i inne przeglądarki) może przy nawigacji "wstecz" przywrócić
 * stronę z bfcache (back-forward cache) — zamrożoną migawkę DOM/JS z chwili,
 * gdy użytkownik ją opuścił — zamiast pozwolić SPA normalnie obsłużyć powrót
 * przez własny routing/popstate. To zupełnie inny mechanizm: JS się nie
 * uruchamia od nowa, DOM jest "odmrażany" z zamrożonego stanu. To
 * udokumentowane, konkretne źródło łamania position:fixed na iOS Safari
 * (i ogólnie niespójności stanu SPA) — pasuje dokładnie do zgłoszonego
 * przypadku: powrót na PIERWSZĄ stronę sesji (Home, jedyna, która była
 * kiedykolwiek "prawdziwym" wejściem z serwera, nie nawigacją SPA) psuje
 * fixed, podczas gdy zwykłe wstecz między stronami SPA (np. produkt→
 * kategoria) działa poprawnie.
 *
 * Jedyny w pełni niezawodny sposób odzyskania spójnego stanu po przywróceniu
 * z bfcache to pełne przeładowanie — i robimy to WYŁĄCZNIE wtedy, gdy
 * przeglądarka jawnie to zgłasza (event.persisted), nigdy profilaktycznie
 * ani nie "po każdej nawigacji wstecz".
 */
function ReloadOnBfcacheRestore() {
  useEffect(() => {
    const handler = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);
  return null;
}

function Router() {
  const [location] = useLocation();
  return (
    // resetKey (NIE React key!) — nawigacja na inną trasę po złapanym błędzie
    // odzyskuje granicę samą w sobie (componentDidUpdate), bez wymuszania
    // pełnego unmount/remount CAŁEJ podstrony na każdej, nawet bezbłędnej,
    // nawigacji. Patrz komentarz w ErrorBoundary.tsx.
    <ErrorBoundary resetKey={location}>
    <Suspense fallback={<div className="min-h-[100dvh] bg-background" />}>
      <CmsRouteBoundary>{isUnavailableBouquetCreatorPath(location) ? <CreatorComingSoon /> : <Switch>
        <Route path="/" component={Home} />
        <Route path="/konto" component={AccountPage} />
        <Route path="/checkout" component={CheckoutPage} />
        <Route path="/dziekujemy" component={DziekujemyPage} />
        <Route path="/advisor-tags" component={AdvisorTagsAdmin} />
        <Route path="/kreatory" component={KreatoryCatalog} />
        <Route path="/kreatory-bukietow" component={KreatoryCatalog} />
        <Route path="/kreator/roze" component={KreatorRoze} />
        <Route path="/kreator/tulipany" component={KreatorTulipany} />
        <Route path="/kreator/sloneczniki" component={KreatorSloneczniki} />
        <Route path="/kreator/gerbery" component={KreatorGerbery} />
        <Route path="/kreator/lilie" component={KreatorLilie} />
        <Route path="/kreator/eustomy" component={KreatorEustomy} />
        <Route path="/dostawa" component={DostawaPage} />
        <Route path="/kontakt" component={KontaktPage} />
        <Route path="/regulamin" component={RegulaminPage} />
        <Route path="/prywatnosc" component={PrywatnoscPage} />
        {/* ── Ślub ── */}
        <Route path="/slub" component={SlubPage} />

        {/* ── Kwiaty ── */}
        <Route path="/kwiaty" component={KwiatyPage} />
        {/* Dedicated category pages — must come before /kwiaty/:handle */}
        <Route path="/kosze-kwiatowe" component={KoszeKwiatowePage} />
        <Route path="/kwiaty-w-torebce" component={KwiatyWTorebcePage} />
        <Route path="/lasy-w-szkle" component={LasyWSzklePage} />
        <Route path="/wieczne-roze" component={WieczneRozePage} />
        <Route path="/kwiaty/:handle" component={DynamicCategoryPage} />

        {/* ── Bukiety ── */}
        <Route path="/bukiety" component={BukietyPage} />

        {/* ── Inne główne kategorie ── */}
        <Route path="/flower-boxy" component={FlowerBoxyPage} />
        <Route path="/kolekcje/flowerbox" component={FlowerBoxyPage} />
        <Route path="/kwiaty-doniczkowe" component={KwiatyDoniczkowe} />
        <Route path="/balony-z-helem" component={BalonyzHelemPage} />

        {/* ── Okazje ── */}
        <Route path="/okazje" component={OkazjePage} />
        <Route path="/okazje/:handle" component={DynamicCategoryPage} />
        <Route path="/dla-kogo" component={DlaKogoHub} />
        <Route path="/dla-kogo/:handle" component={DynamicCategoryPage} />

        {/* ── Kompozycje pogrzebowe ── */}
        <Route path="/pogrzeb" component={PogrzebPage} />
        <Route path="/kompozycje-pogrzebowe" component={PogrzebPage} />
        <Route path="/bukiety-pogrzebowe">
          {() => <ClientCanonicalRedirect to={collectionUrl('bukiety-pogrzebowe')} />}
        </Route>
        <Route path="/wience-pogrzebowe">
          {() => <ClientCanonicalRedirect to={collectionUrl('wience-pogrzebowe')} />}
        </Route>
        <Route path="/wiazanki-pogrzebowe">
          {() => <ClientCanonicalRedirect to={collectionUrl('wiazanki-pogrzebowe')} />}
        </Route>
        <Route path="/sztuczne-kompozycje">
          {() => <ClientCanonicalRedirect to={collectionUrl('sztuczne-kompozycje')} />}
        </Route>
        <Route path="/kompozycje-pogrzebowe/bukiety-pogrzebowe">
          {() => <FuneralCollectionPage handle="bukiety-pogrzebowe" />}
        </Route>
        <Route path="/kompozycje-pogrzebowe/wience-pogrzebowe">
          {() => <FuneralCollectionPage handle="wience-pogrzebowe" />}
        </Route>
        <Route path="/kompozycje-pogrzebowe/wiazanki-pogrzebowe">
          {() => <FuneralCollectionPage handle="wiazanki-pogrzebowe" />}
        </Route>
        <Route path="/kompozycje-pogrzebowe/sztuczne-kompozycje">
          {() => <FuneralCollectionPage handle="sztuczne-kompozycje" />}
        </Route>
        <Route path="/kompozycje-pogrzebowe/:handle">{() => <CollectionPage />}</Route>

        {/* ── Bestsellery ── */}
        <Route path="/bestsellery" component={BestseleryPage} />

        {/* ── Generyczny fallback dla kolekcji ── */}
        <Route path="/kolekcje/:handle">{() => <CollectionPage />}</Route>

        {/* ── Legacy /produkty/:handle → /:handle (see ProduktyLegacyRedirect) ── */}
        <Route path="/produkty/:handle" component={ProduktyLegacyRedirect} />

        {/* ── Legacy /collections/* → przekierowanie na publiczny URL ── */}
        <Route path="/collections/:handle" component={CollectionLegacyRedirect} />

        <Route path="/dla-firm" component={DlaFirmPage} />
        <Route path="/polityka-zwrotow" component={PolitykZwrotowPage} />
        <Route path="/faq" component={FaqPage} />
        <Route path="/o-nas" component={ONasPage} />
        <Route path="/blog" component={BlogPage} />
        <Route path="/blog/:slug" component={BlogLegacyRedirect} />
        <Route path="/platnosci" component={PlatnosciPage} />
        <Route path="/zamowienie-specjalne" component={ZamowienieSpecjalnePage} />
        <Route path="/warsztaty-florystyczne" component={WarszytatyPage} />
        <Route path="/sklep" component={SklepPage} />
        <Route path="/kwiaciarnia-lublin" component={KwiaciarniaLublinHub} />
        <Route path="/kwiaciarnia-okolice" component={KwiaciarniaOkoliceHub} />
        <Route path="/kwiaciarnia-lublin/:slug" component={DzielnicaPage} />
        <Route path="/kwiaciarnia-okolice/:slug" component={DzielnicaPage} />
        <Route path="/reset-hasla" component={ResetHaslaPage} />
        <Route path="/ulubione" component={UlubionePage} />
        <Route path="/koszyk" component={KoszykRedirect} />

        {/* ── Produkt — musi być ostatni żeby nie przechwycić innych ścieżek ── */}
        <Route path="/:handle" component={RootContentPage} />
        <Route component={NotFound} />
      </Switch>}</CmsRouteBoundary>
    </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Trasy bez pełnej nawigacji sklepu — checkout ma być skupiony na
 * finalizacji zamówienia (bez topbaru, logo i menu).
 */
const BARE_LAYOUT_ROUTES = new Set(['/checkout']);

function SiteShell() {
  const [location] = useLocation();
  const bareLayout = BARE_LAYOUT_ROUTES.has(location.replace(/\/+$/, '') || '/');
  return (
    <>
      {/* ── Header lives outside #site-root so its background spans full viewport ── */}
      {!bareLayout && <Header />}
      {/* ── Global site container — all content max 1370 px, centred ── */}
      {/* Stable providers keep page state across navigation. */}
      <div
        id="site-root"
        style={{
          maxWidth: 1370,
          margin: '0 auto',
          position: 'relative',
          paddingTop: bareLayout ? 0 : 'var(--header-height, 56px)',
        }}
      >
        <PageBreadcrumbProvider>
          <PageBreadcrumbOutlet top={bareLayout ? 0 : 'var(--header-height, 56px)'} />
          <Suspense fallback={<div className="min-h-[100dvh] bg-background" />}>
            <Router />
          </Suspense>
        </PageBreadcrumbProvider>
      </div>
    </>
  );
}

/** Keep each renderer mounted after its first use, including its exit animation. */
function StorefrontOverlays() {
  const { lastAddedBatch } = useCart();
  // Subscribe before the lazy renderer loads. The toast store retains any
  // notifications received during that download for Toaster's first render.
  const { toasts } = useToast();
  const [cartModalLoaded, setCartModalLoaded] = useState(false);
  const [toasterLoaded, setToasterLoaded] = useState(false);
  const hasCartNotice = lastAddedBatch.length > 0;
  const hasToast = toasts.length > 0;

  useEffect(() => {
    if (hasCartNotice) setCartModalLoaded(true);
    if (hasToast) setToasterLoaded(true);
  }, [hasCartNotice, hasToast]);

  return (
    <>
      {(hasCartNotice || cartModalLoaded) && <Suspense fallback={null}><AddToCartModal /></Suspense>}
      {(hasToast || toasterLoaded) && <Suspense fallback={null}><Toaster /></Suspense>}
    </>
  );
}

function App({ ssrPath, initialTime }: { ssrPath?: string; initialTime?: number } = {}) {
  useEffect(() => { window.dispatchEvent(new Event('bk:hydrated')); }, []);
  return (
    <InitialRenderTime.Provider value={initialTime}>
    <ConsentProvider>
      <CustomerProvider>
        <FavoritesProvider>
          {/* CartProvider is inside WouterRouter so CartContext can use useLocation */}
          <WouterRouter ssrPath={ssrPath} base={getCmsSnapshot()?.preview ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')} hook={getCmsSnapshot()?.preview ? cmsPreviewLocation : undefined}>
            <CartProvider>
              <GoogleAdsMeasurement />
              <ScrollToTop />
              <ResetOverlaysOnNavigate />
              <ReloadOnBfcacheRestore />
              <SiteShell />
              {/* MobileBottomNav lives outside #site-root so sticky bottom:0 spans
                  the full viewport width (not constrained to max-width 1370px) and
                  sticks at the bottom of the body scroll for every route. */}
              <MobileBottomNav />
              <CategoryDrawerLoader />
              {/* Fixed-position overlays stay outside the constrained container */}
              <StorefrontOverlays />
              <CookieConsentManager />
            </CartProvider>
          </WouterRouter>
        </FavoritesProvider>
      </CustomerProvider>
    </ConsentProvider>
    </InitialRenderTime.Provider>
  );
}

export default App;
